import { useEffect, useRef, useState } from 'react';

export interface Microphone {
  deviceId: string;
  label: string;
}

export const useMicrophones = (startMuted: boolean) => {
  const [, setMicrophones] = useState<Microphone[]>([]);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isMuted, setIsMuted] = useState<boolean>(startMuted);
  const [selectedMicrophone, setSelectedMicrophone] =
    useState<Microphone | null>(null);

  // Ref tracks the live stream independently of React state so that the
  // devicechange handler (which may fire before state flushes) can detect
  // an already-active stream and avoid killing it.
  const activeStreamRef = useRef<MediaStream | null>(null);

  const getMicrophones = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices
        .filter((device) => device.kind === 'audioinput')
        .map((device) => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId}`,
        }));

      setMicrophones(audioInputs);
      return audioInputs;
    } catch (error) {
      console.error('Error fetching microphones:', error);
    }
  };

  const connectMicrophone = async (force = false) => {
    try {
      // If we already have a live stream, return it.  This prevents the
      // Safari "devicechange" race: granting mic permission for the first
      // time fires devicechange (device labels become available), which
      // would otherwise call connectMicrophone() a second time and stop the
      // tracks of the stream that is already attached to the WebRTC peer
      // connection.
      if (!force && activeStreamRef.current) {
        const hasLive = activeStreamRef.current
          .getAudioTracks()
          .some((t) => t.readyState === 'live');
        if (hasLive) {
          return activeStreamRef.current;
        }
      }

      const devices = await getMicrophones();

      if (stream && !force) {
        stream.getTracks().forEach((track) => track.stop());
      }

      const defaultMicrophone =
        devices?.find((d) => d.deviceId === 'default') || devices?.[0];

      const newStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          noiseSuppression: true,
          echoCancellation: true,
          autoGainControl: true,
        },
      });

      newStream.getAudioTracks().forEach((track) => {
        track.enabled = !isMuted;
      });

      activeStreamRef.current = newStream;
      setSelectedMicrophone(defaultMicrophone || null);
      setStream(newStream);

      return newStream;
    } catch (error) {
      console.error('Error accessing microphone:', error);
      return null;
    }
  };

  const muteMicrophone = (mute: boolean) => {
    if (stream) {
      stream.getAudioTracks().forEach((track) => {
        track.enabled = !mute;
      });
      setIsMuted(mute);
    }
  };

  const disconnectMicrophone = () => {
    activeStreamRef.current = null;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
      setSelectedMicrophone(null);
    }
  };

  const handleConnection = () => {
    connectMicrophone().catch((error) => {
      console.error('Error connecting to microphone:', error);
    });
  };

  useEffect(() => {
    navigator.mediaDevices.addEventListener('devicechange', handleConnection);

    return () => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        handleConnection,
      );
    };
  }, [selectedMicrophone]);

  // Clean up the mic stream only on unmount.  We intentionally do NOT clean
  // up when `stream` changes, because a new stream can arrive (e.g. from a
  // Safari devicechange event) while the *previous* stream's track is already
  // attached to a live WebRTC peer connection.  Stopping that track would kill
  // the mic audio mid-session.  Explicit cleanup is handled by callers via
  // disconnectMicrophone() and by stopWebrtc() stopping sender tracks.
  useEffect(() => {
    return () => {
      if (activeStreamRef.current) {
        activeStreamRef.current.getTracks().forEach((t) => t.stop());
        activeStreamRef.current = null;
      }
    };
  }, []);

  return {
    selectedMicrophone,
    isMuted,
    stream,
    connectMicrophone,
    muteMicrophone,
    disconnectMicrophone,
  };
};
